import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { User, Clock, TrendingUp, DollarSign, Award, Briefcase, Calendar, CheckCircle2, AlertCircle, Users, BarChart3, ArrowRight } from 'lucide-react'
import { AddButton, ActionControls, Modal, ConfirmDialog, FormInput, FormSelect } from '../components/common/CRUDControls'
import { useHRStore } from '../store/hrStore'

export default function HRPayrollPage() {
  const navigate = useNavigate()
  // Store
  const {
    employees,
    departments,
    payrollHistory,
    leaveRequests,
    performanceReviews,
    addEmployee,
    updateEmployee,
    deleteEmployee,
    addDepartment,
    updateDepartment,
    deleteDepartment,
    getTotalEmployees,
    getActiveLeaves,
    getPendingLeaves,
    getPayrollStats,
  } = useHRStore()

  const stats = useMemo(() => ({
    totalEmployees: getTotalEmployees(),
    activeLeaves: getActiveLeaves(),
    payrollRuns: payrollHistory.length,
    performanceReviews: performanceReviews.length,
  }), [getTotalEmployees, getActiveLeaves, payrollHistory.length, performanceReviews.length])

  // Modal States
  const [showDeptModal, setShowDeptModal] = useState(false)
  const [showEmpModal, setShowEmpModal] = useState(false)
  const [showDeleteDept, setShowDeleteDept] = useState(false)
  const [showDeleteEmp, setShowDeleteEmp] = useState(false)
  const [selectedDept, setSelectedDept] = useState(null)
  const [selectedEmp, setSelectedEmp] = useState(null)
  const [deptForm, setDeptForm] = useState({ name: '', headcount: 0, openRoles: 0, budget: 0 })
  const [empForm, setEmpForm] = useState({ name: '', email: '', department: 'Engineering', role: '', salary: 0, status: 'Active', phone: '', address: '', hireDate: '', manager: '' })

  // Department Handlers
  const handleAddDept = () => {
    setDeptForm({ name: '', headcount: 0, openRoles: 0, budget: 0 })
    setSelectedDept(null)
    setShowDeptModal(true)
  }

  const handleEditDept = (dept) => {
    setSelectedDept(dept)
    setDeptForm(dept)
    setShowDeptModal(true)
  }

  const handleDeleteDept = (dept) => {
    setSelectedDept(dept)
    setShowDeleteDept(true)
  }

  const handleSaveDept = () => {
    if (selectedDept) {
      updateDepartment(selectedDept.id, deptForm)
    } else {
      addDepartment(deptForm)
    }
    setShowDeptModal(false)
  }

  const handleConfirmDeleteDept = () => {
    deleteDepartment(selectedDept.id)
    setShowDeleteDept(false)
  }

  // Employee Handlers
  const handleAddEmp = () => {
    setEmpForm({ name: '', email: '', department: 'Engineering', role: '', salary: 0, status: 'Active', phone: '', address: '', hireDate: '', manager: '' })
    setSelectedEmp(null)
    setShowEmpModal(true)
  }

  const handleEditEmp = (emp) => {
    setSelectedEmp(emp)
    setEmpForm(emp)
    setShowEmpModal(true)
  }

  const handleDeleteEmp = (emp) => {
    setSelectedEmp(emp)
    setShowDeleteEmp(true)
  }

  const handleSaveEmp = () => {
    if (!empForm.name || !empForm.email) {
      alert('Please fill in all required fields')
      return
    }
    if (selectedEmp) {
      updateEmployee(selectedEmp.id, empForm)
    } else {
      addEmployee(empForm)
    }
    setShowEmpModal(false)
  }

  const handleConfirmDeleteEmp = () => {
    deleteEmployee(selectedEmp.id)
    setShowDeleteEmp(false)
  }

  const features = [
    {
      title: '👤 Employee Directory',
      description: 'Comprehensive employee database with profiles, contact info, and employment history',
      icon: User,
      color: 'from-blue-600 to-blue-500',
      stats: '847 Employees',
      path: '/employees'
    },
    {
      title: '⏱️ Time & Attendance',
      description: 'Track time entries, attendance, and generate automated timesheets',
      icon: Clock,
      color: 'from-cyan-600 to-cyan-500',
      stats: 'Real-time Tracking',
      path: '/time-attendance'
    },
    {
      title: '💰 Payroll Management',
      description: 'Process payroll, manage taxes, benefits, and generate payslips automatically',
      icon: DollarSign,
      color: 'from-indigo-600 to-indigo-500',
      stats: '12 Payroll Runs',
      path: '/payroll'
    },
    {
      title: '🏆 Performance Reviews',
      description: 'Conduct 360-degree reviews, set goals, and track employee development',
      icon: Award,
      color: 'from-teal-600 to-teal-500',
      stats: '156 Reviews',
      path: '/performance'
    },
    {
      title: '📋 Leave & Absence',
      description: 'Manage vacation, sick days, and absence requests with approval workflows',
      icon: Calendar,
      color: 'from-purple-600 to-purple-500',
      stats: '34 Active Leaves',
      path: '/leave-management'
    },
    {
      title: '🎓 Onboarding & Training',
      description: 'Automated onboarding workflows and employee training management',
      icon: Briefcase,
      color: 'from-orange-600 to-orange-500',
      stats: '23 New Hires',
      path: '/onboarding'
    },
    {
      title: '📊 HR Analytics',
      description: 'Data-driven insights on employee metrics, turnover, and organizational health',
      icon: BarChart3,
      color: 'from-pink-600 to-pink-500',
      stats: 'Real-time Reports',
      path: '/hr-analytics'
    },
    {
      title: '✓ Benefits Management',
      description: 'Manage health insurance, retirement plans, and employee benefits',
      icon: CheckCircle2,
      color: 'from-amber-600 to-amber-500',
      stats: 'Complete Coverage',
      path: '/benefits'
    },
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900">
      <div className="space-y-6 p-8">

        {/* Header */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-blue-600 via-blue-700 to-cyan-600 p-8 text-white shadow-2xl border border-blue-500/20">
          <div className="absolute top-0 right-0 w-96 h-96 bg-blue-400/10 rounded-full -mr-48 -mt-48 blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-cyan-400/10 rounded-full -ml-40 -mb-40 blur-3xl"></div>

          <div className="relative z-10">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center space-x-3 mb-2">
                  <User className="w-8 h-8 text-yellow-300" />
                  <h1 className="text-5xl font-black">HR / Payroll / Employees</h1>
                </div>
                <p className="text-blue-100 text-lg font-semibold">Enterprise Human Resources & Payroll Management</p>
                <p className="text-blue-200 text-sm mt-1">BambooHR/Workday Level Features - Complete Employee Lifecycle Management</p>
              </div>
              <div className="text-right">
                <div className="text-4xl font-black text-yellow-300">{stats.totalEmployees}</div>
                <div className="text-sm text-blue-100">Total Employees</div>
              </div>
            </div>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Total Employees', value: stats.totalEmployees, icon: User, color: 'from-blue-600 to-blue-500', textColor: 'text-blue-400' },
            { label: 'Active Leave Requests', value: stats.activeLeaves, icon: Calendar, color: 'from-cyan-600 to-cyan-500', textColor: 'text-cyan-400' },
            { label: 'Payroll Cycles', value: stats.payrollRuns, icon: DollarSign, color: 'from-indigo-600 to-indigo-500', textColor: 'text-indigo-400' },
            { label: 'Reviews Completed', value: `${stats.performanceReviews}+`, icon: Award, color: 'from-teal-600 to-teal-500', textColor: 'text-teal-400' },
          ].map((stat, idx) => {
            const Icon = stat.icon
            return (
              <div key={idx} className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 hover:border-blue-400/40 transition-all shadow-lg hover:shadow-blue-500/20">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-300 text-sm font-semibold">{stat.label}</p>
                    <p className={`${stat.textColor} text-4xl font-black mt-3`}>{stat.value}</p>
                  </div>
                  <div className={`bg-gradient-to-br ${stat.color} p-4 rounded-xl shadow-lg shadow-blue-500/20`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {/* HR Management Features */}
        <div>
          <h2 className="text-3xl font-bold text-white mb-6 flex items-center space-x-3">
            <Users className="w-7 h-7 text-blue-400" />
            <span>HR Management Tools</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {features.map((feature, idx) => {
              const Icon = feature.icon
              return (
                <div
                  key={idx}
                  onClick={() => navigate(feature.path)}
                  className="group relative overflow-hidden rounded-xl bg-gradient-to-br from-slate-800 to-slate-900 border border-blue-500/20 p-6 hover:border-blue-400/40 transition-all cursor-pointer shadow-lg hover:shadow-blue-500/20 hover:-translate-y-1"
                >
                  <div className={`h-1 w-full bg-gradient-to-r ${feature.color} rounded-full mb-4 shadow-lg`}></div>

                  <div className={`bg-gradient-to-br ${feature.color} p-3 rounded-lg w-fit mb-4 shadow-lg`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>

                  <h3 className="font-bold text-white mb-2 text-lg">{feature.title}</h3>
                  <p className="text-blue-300 text-sm mb-4">{feature.description}</p>

                  <div className="flex items-center justify-between pt-4 border-t border-blue-500/20">
                    <span className="text-cyan-400 text-sm font-semibold">{feature.stats}</span>
                    <button className="opacity-0 group-hover:opacity-100 transition-opacity">
                      <ArrowRight className="w-4 h-4 text-blue-400" />
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Employee Overview & Performance */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Department Overview */}
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-white flex items-center space-x-2 text-lg">
                <Briefcase className="w-5 h-5 text-blue-400" />
                <span>Department Headcount</span>
              </h3>
              <AddButton onClick={handleAddDept} label="Add" />
            </div>
            <div className="space-y-3">
              {departments.map((dept) => (
                <div key={dept.id} className="bg-slate-700/50 rounded-lg p-4 border border-blue-500/20">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-white font-semibold text-sm">{dept.name}</p>
                    <div className="flex items-center gap-4">
                      <span className="text-cyan-400 font-bold">{dept.headcount} employees</span>
                      <ActionControls onEdit={() => handleEditDept(dept)} onDelete={() => handleDeleteDept(dept)} />
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <div className="w-full bg-slate-600 rounded-full h-2 mr-3">
                      <div className="h-2 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full" style={{width: `${Math.min((dept.headcount / 234) * 100, 100)}%`}}></div>
                    </div>
                    <span className="text-orange-400">{dept.openRoles} open</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Upcoming Events */}
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 shadow-lg">
            <h3 className="font-bold text-white mb-4 flex items-center space-x-2 text-lg">
              <Calendar className="w-5 h-5 text-blue-400" />
              <span>Upcoming HR Events</span>
            </h3>
            <div className="space-y-3">
              {[
                { event: 'Payroll Run', date: 'April 28', type: 'PAYROLL', team: 'Finance' },
                { event: 'Q2 Performance Reviews', date: 'May 5-15', type: 'REVIEW', team: 'All Depts' },
                { event: 'Employee Training Program', date: 'May 20', type: 'TRAINING', team: 'Everyone' },
                { event: 'Annual Benefits Enrollment', date: 'May 25', type: 'BENEFITS', team: 'All' },
              ].map((item, idx) => (
                <div key={idx} className="bg-slate-700/50 rounded-lg p-3 border border-blue-500/20">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-white font-semibold text-sm">{item.event}</p>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                      item.type === 'PAYROLL' ? 'bg-green-500/20 text-green-300' :
                      item.type === 'REVIEW' ? 'bg-blue-500/20 text-blue-300' :
                      item.type === 'TRAINING' ? 'bg-indigo-500/20 text-indigo-300' :
                      'bg-cyan-500/20 text-cyan-300'
                    }`}>
                      {item.type}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-blue-300 text-xs">{item.date}</span>
                    <span className="text-cyan-400 text-xs font-semibold">{item.team}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Manage Employees */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 shadow-lg">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-white flex items-center space-x-3">
              <User className="w-6 h-6 text-blue-400" />
              <span>Manage Employees</span>
            </h2>
            <AddButton onClick={handleAddEmp} label="Add Employee" />
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-blue-500/20">
                  <th className="text-left py-3 px-4 text-blue-300 font-semibold">Name</th>
                  <th className="text-left py-3 px-4 text-blue-300 font-semibold">Email</th>
                  <th className="text-left py-3 px-4 text-blue-300 font-semibold">Department</th>
                  <th className="text-left py-3 px-4 text-blue-300 font-semibold">Role</th>
                  <th className="text-left py-3 px-4 text-blue-300 font-semibold">Salary</th>
                  <th className="text-left py-3 px-4 text-blue-300 font-semibold">Status</th>
                  <th className="text-center py-3 px-4 text-blue-300 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {employees.map((emp) => (
                  <tr key={emp.id} className="border-b border-blue-500/10 hover:bg-slate-700/30 transition-colors">
                    <td className="py-3 px-4 text-white font-semibold">{emp.name}</td>
                    <td className="py-3 px-4 text-blue-300">{emp.email}</td>
                    <td className="py-3 px-4 text-blue-300">{emp.department}</td>
                    <td className="py-3 px-4 text-blue-300">{emp.role}</td>
                    <td className="py-3 px-4 text-cyan-400 font-semibold">${(emp.salary / 1000).toFixed(0)}K</td>
                    <td className="py-3 px-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                        emp.status === 'Active' ? 'bg-green-500/20 text-green-300' : 'bg-yellow-500/20 text-yellow-300'
                      }`}>
                        {emp.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 flex items-center justify-center">
                      <ActionControls
                        onEdit={() => handleEditEmp(emp)}
                        onDelete={() => handleDeleteEmp(emp)}
                        size="lg"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Department Modal */}
        <Modal
          isOpen={showDeptModal}
          onClose={() => setShowDeptModal(false)}
          title={selectedDept ? 'Edit Department' : 'Add Department'}
          onSubmit={handleSaveDept}
          submitLabel={selectedDept ? 'Update' : 'Add'}
        >
          <FormInput
            label="Department Name"
            value={deptForm.name}
            onChange={(e) => setDeptForm({ ...deptForm, name: e.target.value })}
            placeholder="e.g., Engineering"
            required
          />
          <FormInput
            label="Headcount"
            type="number"
            value={deptForm.headcount}
            onChange={(e) => setDeptForm({ ...deptForm, headcount: parseInt(e.target.value) || 0 })}
            required
          />
          <FormInput
            label="Open Roles"
            type="number"
            value={deptForm.openRoles}
            onChange={(e) => setDeptForm({ ...deptForm, openRoles: parseInt(e.target.value) || 0 })}
          />
          <FormInput
            label="Department Budget"
            type="number"
            value={deptForm.budget}
            onChange={(e) => setDeptForm({ ...deptForm, budget: parseInt(e.target.value) || 0 })}
            placeholder="e.g., 1000000"
          />
        </Modal>

        {/* Employee Modal */}
        <Modal
          isOpen={showEmpModal}
          onClose={() => setShowEmpModal(false)}
          title={selectedEmp ? 'Edit Employee' : 'Add Employee'}
          onSubmit={handleSaveEmp}
          submitLabel={selectedEmp ? 'Update' : 'Add'}
        >
          <FormInput
            label="Full Name"
            value={empForm.name}
            onChange={(e) => setEmpForm({ ...empForm, name: e.target.value })}
            placeholder="John Smith"
            required
          />
          <FormInput
            label="Email"
            type="email"
            value={empForm.email}
            onChange={(e) => setEmpForm({ ...empForm, email: e.target.value })}
            placeholder="john@company.com"
            required
          />
          <FormInput
            label="Phone"
            value={empForm.phone}
            onChange={(e) => setEmpForm({ ...empForm, phone: e.target.value })}
            placeholder="555-0101"
          />
          <FormInput
            label="Address"
            value={empForm.address}
            onChange={(e) => setEmpForm({ ...empForm, address: e.target.value })}
            placeholder="123 Main St"
          />
          <FormSelect
            label="Department"
            value={empForm.department}
            onChange={(e) => setEmpForm({ ...empForm, department: e.target.value })}
            options={departments.map(d => ({ value: d.name, label: d.name }))}
          />
          <FormInput
            label="Role"
            value={empForm.role}
            onChange={(e) => setEmpForm({ ...empForm, role: e.target.value })}
            placeholder="e.g., Senior Engineer"
            required
          />
          <FormInput
            label="Salary"
            type="number"
            value={empForm.salary}
            onChange={(e) => setEmpForm({ ...empForm, salary: parseInt(e.target.value) || 0 })}
            placeholder="95000"
          />
          <FormInput
            label="Manager"
            value={empForm.manager}
            onChange={(e) => setEmpForm({ ...empForm, manager: e.target.value })}
            placeholder="e.g., Alice Johnson"
          />
          <FormInput
            label="Hire Date"
            type="date"
            value={empForm.hireDate}
            onChange={(e) => setEmpForm({ ...empForm, hireDate: e.target.value })}
          />
          <FormSelect
            label="Status"
            value={empForm.status}
            onChange={(e) => setEmpForm({ ...empForm, status: e.target.value })}
            options={[
              { value: 'Active', label: 'Active' },
              { value: 'On Leave', label: 'On Leave' },
              { value: 'Inactive', label: 'Inactive' },
            ]}
          />
        </Modal>

        {/* Delete Confirmations */}
        <ConfirmDialog
          isOpen={showDeleteDept}
          onConfirm={handleConfirmDeleteDept}
          onCancel={() => setShowDeleteDept(false)}
          title="Delete Department"
          message={`Are you sure you want to delete ${selectedDept?.name}? This action cannot be undone.`}
        />

        <ConfirmDialog
          isOpen={showDeleteEmp}
          onConfirm={handleConfirmDeleteEmp}
          onCancel={() => setShowDeleteEmp(false)}
          title="Delete Employee"
          message={`Are you sure you want to delete ${selectedEmp?.name}? This action cannot be undone.`}
        />

      </div>
    </div>
  )
}
